import { Sun } from "lucide-react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import "./Solutions.css";
import residentialImg from "../assets/download.jpg";
import commercialImg from "../assets/download (2).jpg";
import industrialImg from "../assets/download (3).jpg";
import storageImg from "../assets/battery.jpg";
import evImg from "../assets/evcharging.png";

function Solutions() {
  const solutions = [
    {
      title: "Residential Solar",
      description: "Power your home with clean, affordable, and reliable solar energy.",
    },
    {
      title: "Commercial Solar",
      description: "Reduce operational costs with scalable solar solutions for businesses.",
    },
    {
      title: "Battery Storage",
      description: "Store excess energy and stay powered during outages.",
    },
  ];

  return (
    <>
    <section className="solutions">
      <h2 className="solutions-title">Our Solar Solutions</h2>

      <div className="solutions-grid">
        {solutions.map((item) => (
          <Card key={item.title} className="solution-card" elevation={0}>
            <CardContent>
              <div className="solution-icon">
                <Sun size={28} />
              </div>

              <h3 className="solution-title">{item.title}</h3>
              <p className="solution-text">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
   
 
    <section className="solutions">

      {/* Residential */}
      <div className="solution-section">
        <div className="solution-row">
          <div className="solution-content">
            <h2>🏠 Residential Solar Solutions</h2>
            <h4>Clean, Reliable Solar Energy for Your Home</h4>

            <p>
              Residential solar solutions help homeowners reduce electricity bills,
              gain energy independence, and contribute to a cleaner environment.
              Our rooftop solar systems are designed to maximize energy generation
              while blending seamlessly with your home.
            </p>

            <h5>What We Offer</h5>
            <ul>
              <li>Rooftop solar panel installations</li>
              <li>On-grid, off-grid, and hybrid solar systems</li>
              <li>Home battery storage solutions</li>
              <li>Net metering support and approvals</li>
            </ul>

            <h5>Benefits</h5>
            <ul>
              <li>Save up to 70% on monthly electricity bills</li>
              <li>Reliable power backup during outages</li>
              <li>Low maintenance and long system lifespan</li>
              <li>Increased property value</li>
              <li>Reduced carbon footprint</li>
            </ul>
          </div>
          <div className="solution-image-container">
            <img src={residentialImg} alt="Residential Solar Solutions" className="solution-image" />
          </div>
        </div>
      </div>

      {/* Commercial */}
      <div className="solution-section">
        <div className="solution-row reverse">
          <div className="solution-content">
            <h2>🏢 Commercial Solar Solutions</h2>
            <h4>Smart Solar Solutions for Growing Businesses</h4>

            <p>
              Commercial solar systems help businesses significantly lower operating
              costs while meeting sustainability and ESG goals. Our systems are
              scalable, efficient, and designed for high performance.
            </p>

            <h5>What We Offer</h5>
            <ul>
              <li>Rooftop solar for offices and retail spaces</li>
              <li>Solar solutions for schools, hospitals, and hotels</li>
              <li>Solar carport systems</li>
              <li>Smart energy monitoring systems</li>
            </ul>

            <h5>Benefits</h5>
            <ul>
              <li>Reduced operational electricity costs</li>
              <li>High return on investment (ROI)</li>
              <li>Improved brand sustainability image</li>
              <li>Long-term energy cost stability</li>
            </ul>
          </div>
          <div className="solution-image-container">
            <img src={commercialImg} alt="Commercial Solar Solutions" className="solution-image" />
          </div>
        </div>
      </div>

      {/* Industrial */}
      <div className="solution-section">
        <div className="solution-row">
          <div className="solution-content">
            <h2>🏭 Industrial Solar Solutions</h2>
            <h4>High-Capacity Solar Power for Industrial Operations</h4>

            <p>
              Industrial solar solutions are built to handle large energy demands
              with maximum efficiency. Our systems are engineered for factories,
              warehouses, and large-scale power generation needs.
            </p>

            <h5>What We Offer</h5>
            <ul>
              <li>Factory rooftop solar installations</li>
              <li>Ground-mounted solar power plants</li>
              <li>Captive power plants</li>
              <li>Open-access solar solutions</li>
            </ul>

            <h5>Benefits</h5>
            <ul>
              <li>Reduced dependency on grid power</li>
              <li>Stable and predictable energy costs</li>
              <li>High-efficiency large-scale generation</li>
              <li>Long system lifespan and reliability</li>
            </ul>
          </div>
          <div className="solution-image-container">
            <img src={industrialImg} alt="Industrial Solar Solutions" className="solution-image" />
          </div>
        </div>
      </div>

      {/* Energy Storage */}
      <div className="solution-section">
        <div className="solution-row reverse">
          <div className="solution-content">
            <h2>🔋 Energy Storage & Smart Solutions</h2>
            <h4>Store and Manage Your Solar Energy Efficiently</h4>

            <p>
              Energy storage solutions ensure uninterrupted power supply and allow
              better control over energy usage. Smart systems help monitor
              performance and optimize consumption.
            </p>

            <h5>What We Offer</h5>
            <ul>
              <li>Lithium-ion battery storage systems</li>
              <li>Solar inverters and controllers</li>
              <li>Smart energy monitoring solutions</li>
              <li>Backup power systems</li>
            </ul>

            <h5>Benefits</h5>
            <ul>
              <li>Continuous power during outages</li>
              <li>Real-time energy monitoring</li>
              <li>Improved energy efficiency</li>
              <li>Reduced reliance on grid electricity</li>
            </ul>
          </div>
          <div className="solution-image-container">
            <img src={storageImg} alt="Energy Storage Solutions" className="solution-image" />
          </div>
        </div>
      </div>

      {/* EV Charging */}
      <div className="solution-section">
        <div className="solution-row">
          <div className="solution-content">
            <h2>🚗 EV Charging & Future Energy Solutions</h2>
            <h4>Solar-Powered Charging for a Sustainable Future</h4>

            <p>
              Our EV charging solutions support clean mobility by integrating solar
              power with electric vehicle infrastructure for homes and businesses.
            </p>

            <h5>What We Offer</h5>
            <ul>
              <li>Home EV charging stations</li>
              <li>Commercial EV charging infrastructure</li>
              <li>Solar carport EV chargers</li>
              <li>Microgrid and smart grid integration</li>
            </ul>

            <h5>Benefits</h5>
            <ul>
              <li>Lower EV charging costs</li>
              <li>Clean and sustainable transportation</li>
              <li>Future-ready energy infrastructure</li>
              <li>Reduced carbon emissions</li>
            </ul>
          </div>
          <div className="solution-image-container">
            <img src={evImg} alt="EV Charging Solutions" className="solution-image" />
          </div>
        </div>
      </div>

    </section>
    </>
    
  );
}

export default Solutions;
